
export class GrooveConsole {
    // TEMPO FLUIDITY state
    private lastTouchTime: number = 0;
    private touchDensity: number = 0; // Touches per second (smoothed)

    // HUMANIZATION (Feature 7: The Virtuoso)
    private rubatoPhase: number = 0; // 0 to 2PI

    constructor() { }

    /**
     * Returns a dynamic tempo multiplier based on user activity AND Rubato.
     * < 0.8 = Slow/Rubato (Low density)
     * 1.0 = Normal
     * > 1.2 = Accelerando (High density)
     */
    public getAdaptiveTempoMultiplier(detectedBPM: number | null = null): number {
        // V11 FIX: Tempo Sync (The Drifting Band Resolution)
        // If we have a confident BPM detected from the user, we MUST sync to it.
        // We assume the internal base tempo is 120 BPM.
        // So if user plays at 60, mult should be 0.5.
        // If user plays at 180, mult should be 1.5.

        let multiplier = 1.0;

        if (detectedBPM !== null) {
            // Clamp to reasonable limits (0.5x to 2.0x) to avoid extreme absurdity
            // 60 BPM -> 0.5
            // 120 BPM -> 1.0
            // 240 BPM -> 2.0
            const rawMult = detectedBPM / 120;
            multiplier = Math.max(0.5, Math.min(2.0, rawMult));
        } else {
            // FALLBACK: Density Drive (Legacy behavior)
            // Map density (0 - 4 TPS) to Multiplier (0.7 - 1.3)
            if (this.touchDensity < 0.5) {
                multiplier = 0.8;
            } else if (this.touchDensity > 2.5) {
                multiplier = 1.2;
            } else {
                // Linear in between
                multiplier = 0.8 + ((this.touchDensity - 0.5) / 2.0) * 0.4;
            }
        }

        // 2. Rubato "Breathing" (Sine Wave)
        // Adds +/- 5% tempo fluctuation (reduced from 10%) for human feel
        // Don't apply heavy rubato if we are already locked to user BPM (user provides the rubato!)
        this.rubatoPhase += 0.01;
        const breatheAmount = detectedBPM !== null ? 0.02 : 0.05; // Less drift if locked
        const breathe = Math.sin(this.rubatoPhase) * breatheAmount;

        multiplier += breathe;

        return Math.max(0.5, Math.min(2.0, multiplier));
    }

    public trackTouch() {
        // Track Density
        const now = Date.now() / 1000;
        if (this.lastTouchTime > 0) {
            const delta = now - this.lastTouchTime;
            if (delta > 0.01) { // Avoid div by zero
                const instantDensity = 1 / delta;
                // Smooth: 90% history, 10% new
                this.touchDensity = (this.touchDensity * 0.9) + (instantDensity * 0.1);
            } else {
                // Very fast burst, count as high density
                this.touchDensity = (this.touchDensity * 0.9) + (10 * 0.1);
            }
        }
        this.lastTouchTime = now;
    }

    public getTouchDensity(): number {
        return this.touchDensity;
    }

    /**
     * HUMANIZATION: Dynamics
     * Adds random variation to velocity to sound less robotic.
     * Accents the 'one' (or strong beats) if specified.
     */
    public humanizeVelocity(baseVelocity: number, isStrongBeat: boolean = false): number {
        // 1. Random variance (+/- 10%)
        const variance = (Math.random() * 0.2) - 0.1;
        let newVel = baseVelocity * (1 + variance);

        // 2. Strong Beat Accent (+15%)
        if (isStrongBeat) {
            newVel *= 1.15;
        }

        // Clamp to MIDI range
        return Math.max(0.1, Math.min(1.0, newVel));
    }

    /**
     * HUMANIZATION: Micro-Timing (Groove Pocket)
     * Adds subtle timing offsets so chords/notes don't hit exactly on the grid.
     * @param instrumentRole 'bass' | 'chord' | 'melody'
     */
    public humanizeTiming(baseDelay: number, instrumentRole: 'bass' | 'chord' | 'melody' = 'melody'): number {
        let offset = 0;

        // GROOVE POCKETS
        if (instrumentRole === 'bass') {
            // ON THE GRID (The Anchor)
            offset = (Math.random() * 0.010) - 0.005;
        } else if (instrumentRole === 'chord') {
            // LAID BACK (The Bed)
            // 10-30ms late per note to feel "strummed" or heavy
            offset = 0.020 + (Math.random() * 0.015);
        } else {
            // MELODY (The Singer)
            // Loose.
            offset = (Math.random() * 0.030) - 0.015;
        }

        return Math.max(0, baseDelay + offset);
    }

    public reset() {
        this.touchDensity = 0;
        this.lastTouchTime = 0;
        this.rubatoPhase = 0;
    }
}
